import { container } from 'tsyringe'
import Vue from 'vue'
import {
  VuexModule,
  Module,
  Mutation,
  Action,
  getModule,
} from 'vuex-module-decorators'
import store from '@/app/ui/store'
import {
  EventBus,
  EventBusConstants,
  LocalStorage,
  Utils,
} from '@/app/infrastructures/misc'
import { PickupPresenter } from '@/app/ui/presenters/PickupPresenter'
import {
  NearbyCourier,
  Pickup,
  PickupDetail,
  PickupDetailSnapshot,
  PickupForceDetail,
  PickupSummary,
  Pickups,
  PickupAdditionalTime,
  PickupAdditionalTimes,
  PickupCRROTWV2List,
  PickupCRROTWV2Summary,
  PickupCRRQUEs,
  PickupCRRNFDSummary,
  PickupNfdList,
  PickupNfdShipperHashDetail,
  PickupNfdSummary,
  PickupShipperHashOutcome,
  PickupCorporate,
  PickupCorporates,
  PickupCorporateSummary,
  DashboardPickupTransferOutcome,
  PickupCRROTWs,
  AssignPickupBulkHistoryList,
  AssignPickupBulkHistoryPagination,
  PickupAssignIds,
  AdminContactShow,
  PickupCorporatesV2,
  PickupCorporateV2,
  PickupCorporateNfdDeadlineSummary,
} from '@/domain/entities/Pickup'
import { ManageCourier } from '@/domain/entities/ManageCourier'
import dayjs from 'dayjs'
import {
  AdminTaskPickupCancelApiRequest,
  AdminTaskPickupDoneApiRequest,
  CancelPickupApiRequest,
  ExportPickupApiRequest,
  ExportPickupRequest,
  ForceCancelCRRQUERequest,
  ForceCRRSRCRequest,
  ManualAssigneeApiRequest,
  MarkAsDoneApiRequest,
  PickupDedicatedCRROTWRequest,
  PickupForceCRRCNCRequest,
  UpdateIsContactedCRROTWRequest,
  UpdateIsContactedCRRQUERequest,
  DashboardPickupTransferRequest,
  ReadAssignPickupBulkHistoryRequest,
  ManualAssigneeBulkApiRequest,
} from '@/data/payload/api/PickupRequest'
import { PODTrackerPresenter } from '../presenters/PODTrackerPresenter'
import { PODTrackerDrivers } from '@/domain/entities/PODTracker'
import { ManageCourierPresenter } from '../presenters/ManageCourierPresenter'
import { Pagination } from '@/domain/entities/Pagination'
import MainController from './MainController'

export interface DropdownValue {
  value: number
  label: string
}

interface MarkAsDoneForm {
  statusId: string | string[]
  groupId: string | undefined
  shipmentId: string | undefined
  courierId: number
  quantity: number
  hubPersonName: string
  personName: string
  relationId?: string
}

export interface PickupState {
  isLoading: boolean
  isExportLoading: boolean
  isExportCRRNFDLoading: boolean
  isExportCRROTWLoading: boolean
  isExportCRRQUELoading: boolean
  isExportCRRDONLoading: boolean
  isExportCRRPICLoading: boolean
  isExportDOXLoading: boolean
  isCourierLoading: boolean
  isexportPickupListLoading: boolean
  isSuccessForce: boolean
  pickupData: Pickup[]
  pickupDetail: PickupDetail
  pickupDetails: PickupDetail[]
  pickupSnapshotDetail: PickupDetailSnapshot[]
  pickupSummary: PickupSummary
  nearbyCourierData: NearbyCourier[]
  courierData: DropdownValue[]
  latestSync: string | null
  pickupBulkData: PickupBulkDataState | null
  paginationData: Pagination
  requestManualAssign: string
  pickupAdditionalTimeData: PickupAdditionalTime[]
  pickupCRROTWData: PickupCRROTWs
  pickupCRROTWV2Data: PickupCRROTWV2List
  pickupCRROTWSummaryV2: PickupCRROTWV2Summary
  pickupCRRQUEData: PickupCRRQUEs
  isSuccessForceCRRCNC: boolean
  isFailedForceCRRCNC: boolean
  isForceCRRSRCSuccess: boolean
  isLoadingForceCRRSRC: boolean
  isSuccessForceCancelCRRQUE: boolean
  isFailedForceCancelCRRQUE: boolean
  CRRNFDSummaryData: PickupCRRNFDSummary
  pickupNfdList: PickupNfdList
  pickupNfdSummary: PickupNfdSummary
  pickupNfdShipperHashDetail: PickupNfdShipperHashDetail | null
  isLoadingPickupNfdShipperDetail: boolean
  isLoadingExportManualAssignLog: boolean
  isSuccessExportManualAssignLog: boolean
  pickupCorporateData: PickupCorporate[]
  pickupCorporateSummary: PickupCorporateSummary
  isCancelPickupSuccess: boolean
  isSuccessExportCRRNFD: boolean
  isLoadingExportCorporate: boolean
  isSuccessExportCorporate: boolean
  isLoadingCRROTW: boolean
  isLoadingCRRNFD: boolean
  assignPickupBulkHistoryList: AssignPickupBulkHistoryList
}

export interface PickupBulkDataState {
  bookingIds: string
  courier: DropdownValue
  manualAssignDate?: Date
  manualAssignHour?: number
  manualAssignMinute?: number
  isScheduleManualAssign?: boolean
  plateNo?: string
}

/** Module-level: vuex-module-decorators @Action context is not the class instance — do not use as instance methods */
function buildCrrotwV2ListParams(
  params: Record<string, string | number | boolean>
): Map<string, string | number> {
  const rest = { ...params }
  delete rest.syncSummary
  delete rest.dateFrom
  delete rest.courierCall
  const snake = Utils.toInstance(
    {} as Record<string, unknown>,
    JSON.stringify(rest),
    'snake_case'
  ) as Record<string, unknown>

  const map = new Map<string, string | number>()
  if (snake.page != null) {
    map.set('page', Number(snake.page))
  }
  if (snake.per_page != null) {
    map.set('per_page', Number(snake.per_page))
  }

  const qVal = snake.keyword ?? snake.q
  if (qVal != null && String(qVal).trim() !== '') {
    map.set('q', String(qVal).trim())
  }

  if (snake.shipment_type != null && String(snake.shipment_type) !== '') {
    map.set('shipment_type', String(snake.shipment_type))
  }

  if (snake.origin_country !== null && String(snake.origin_country) !== '') {
    map.set('origin_country', String(snake.origin_country))
  }

  const db = snake.duration_bucket
  if (
    db != null &&
    String(db) !== '' &&
    String(db) !== 'all' &&
    String(db) !== 'undefined'
  ) {
    map.set('duration_bucket', decodeURIComponent(String(db)))
  }

  const col = String(snake.column ?? '').trim()
  const dirRaw = String(snake.sort_direction ?? 'DESC').toUpperCase()
  map.set('order_by', col !== '' ? col : 'crrotw_at')
  map.set('sort', dirRaw === 'ASC' ? 'ASC' : 'DESC')
  return map
}

function buildCrrotwV2SummaryParams(
  params: Record<string, string | number | boolean>
): Map<string, string | number> {
  const map = new Map<string, string | number>()
  const full = buildCrrotwV2ListParams(params)
  const q = full.get('q')
  const st = full.get('shipment_type')
  const db = full.get('duration_bucket')
  if (q != null) {
    map.set('q', q)
  }
  if (st != null) {
    map.set('shipment_type', st)
  }
  if (db != null) {
    map.set('duration_bucket', db)
  }
  return map
}

const PICKUP_NFD_ORDER_FIELDS = new Set([
  'sender_name',
  'total_shipments',
  'crrnfd_at',
  'crrnfd_duration_minute',
  'crrnfd_iteration',
  'courier_name',
  'last_cancel_reason',
])

function buildPickupNfdListParams(
  params: Record<string, string | number | boolean | undefined>
): Map<string, string | number> {
  const map = new Map<string, string | number>()
  if (params.page != null) {
    map.set('page', Math.max(1, Number(params.page)))
  }
  if (params.perPage != null) {
    map.set('per_page', Math.max(1, Number(params.perPage)))
  }

  const qRaw = params.q ?? params.keyword
  if (qRaw != null) {
    const q = String(qRaw).trim()
    if (q) {
      map.set('q', q)
    }
  }

  const st =
    params.shipmentType != null ? String(params.shipmentType) : ''
  if (st) {
    map.set('shipment_type', st)
  }

  const dbRaw = params.durationBucket ?? params.duration_bucket
  if (dbRaw != null) {
    const db = decodeURIComponent(String(dbRaw))
    if (db && db !== 'all' && db !== 'undefined') {
      map.set('duration_bucket', db)
    }
  }

  const sortName = String(
    params.sortField ?? params.order_by ?? ''
  ).trim()
  const orderBy = PICKUP_NFD_ORDER_FIELDS.has(sortName)
    ? sortName
    : 'crrnfd_at'
  map.set('order_by', orderBy)

  const dir = String(
    params.sortDirection ?? params.sort ?? 'DESC'
  ).toUpperCase()
  map.set('sort', dir === 'ASC' ? 'ASC' : 'DESC')

  return map
}

function buildPickupNfdSummaryParams(
  params: Record<string, string | number | boolean | undefined>
): Map<string, string | number> {
  const full = buildPickupNfdListParams(params)
  const map = new Map<string, string | number>()
  const q = full.get('q')
  const st = full.get('shipment_type')
  const db = full.get('duration_bucket')
  if (q != null) {
    map.set('q', q)
  }
  if (st != null) {
    map.set('shipment_type', st)
  }
  if (db != null) {
    map.set('duration_bucket', db)
  }
  return map
}

const PICKUP_NFD_HASH_ORDER_FIELDS = new Set([
  'shipment_id',
  'crrnfd_at',
  'crrnfd_duration_minute',
])

function buildPickupNfdByHashDetailParams(
  params: Record<string, string | number | boolean | undefined>
): Map<string, string | number> {
  const map = new Map<string, string | number>()
  if (params.page != null) {
    map.set('page', Math.max(1, Number(params.page)))
  }
  if (params.perPage != null) {
    const pp = Math.min(100, Math.max(1, Number(params.perPage)))
    map.set('per_page', pp)
  }
  if (params.isShowDetail != null) {
    map.set('is_show_detail', params.isShowDetail ? 'true' : 'false')
  }
  const sortName = String(
    params.sortField ?? params.order_by ?? ''
  ).trim()
  const orderBy = PICKUP_NFD_HASH_ORDER_FIELDS.has(sortName)
    ? sortName
    : ''
  map.set('order_by', orderBy)
  const dir = String(
    params.sortDirection ?? params.sort ?? ''
  ).toUpperCase()
  if(dir === '') {
     map.set('sort', '')
  } else {
    map.set('sort', dir === 'ASC' ? 'ASC' : 'DESC')
  }
  return map
}

@Module({ namespaced: true, store, name: 'pickup', dynamic: true })
class PickupController extends VuexModule implements PickupState {
  private presenter: PickupPresenter = container.resolve(PickupPresenter)
  private podTrackerPresenter: PODTrackerPresenter = container.resolve(
    PODTrackerPresenter
  )
  private manageCourierPresenter: ManageCourierPresenter = container.resolve(
    ManageCourierPresenter
  )
  public isLoading = false
  public isExportLoading = false
  public isExportCRRNFDLoading = false
  public isExportCRROTWLoading = false
  public isExportCRRQUELoading = false
  public isExportCRRDONLoading = false
  public isExportCRRPICLoading = false
  public isExportDOXLoading = false
  public isCourierLoading = false
  public isexportPickupListLoading = false
  public isSuccessForce = false
  public pickupData: Pickup[] = []
  public pickupDetail: PickupDetail = new PickupDetail()
  public pickupDetails: PickupDetail[] = [new PickupDetail()]
  public pickupDetailForce: PickupForceDetail = new PickupForceDetail()
  public pickupSnapshotDetail: PickupDetailSnapshot[] = []
  public pickupSummary: PickupSummary = new PickupSummary()
  public courierData: DropdownValue[] = []
  public nearbyCourierData: NearbyCourier[] = []
  public latestSync: string | null = null
  public pickupBulkData: PickupBulkDataState | null = null
  public paginationData = new Pagination()
  public requestManualAssign = ''
  public pickupAdditionalTimeData: PickupAdditionalTime[] = []
  public pickupCRROTWData: PickupCRROTWs = new PickupCRROTWs()
  public pickupCRROTWV2Data: PickupCRROTWV2List = new PickupCRROTWV2List(
    new Pagination(),
    []
  )
  public pickupCRROTWSummaryV2: PickupCRROTWV2Summary = new PickupCRROTWV2Summary()
  public pickupCRRQUEData: PickupCRRQUEs = new PickupCRRQUEs()
  public isSuccessForceCRRCNC = false
  public isFailedForceCRRCNC = false
  public isForceCRRSRCSuccess = false
  public isLoadingForceCRRSRC = false
  public isSuccessForceCancelCRRQUE = false
  public isFailedForceCancelCRRQUE = false
  public CRRNFDSummaryData: PickupCRRNFDSummary = new PickupCRRNFDSummary(0, 0, 0, 0, 0, 0)
  public pickupNfdList: PickupNfdList = new PickupNfdList(
    new Pagination(1, 10, 0),
    []
  )
  public pickupNfdSummary: PickupNfdSummary =
    new PickupNfdSummary()
  public pickupNfdShipperHashDetail: PickupNfdShipperHashDetail | null = null
  public isLoadingPickupNfdShipperDetail = false
  public isLoadingExportManualAssignLog = false
  public isSuccessExportManualAssignLog = false
  public pickupCorporateData: PickupCorporate[] = []
  public pickupCorporateDataV2: PickupCorporateV2[] = []
  // Monotonic request tokens to drop stale responses (e.g. rapid tab switch).
  // Only the response matching the latest token is committed to state.
  public pickupCorporateListV2RequestToken = 0
  public pickupCorporateNfdDeadlineSummaryRequestToken = 0
  public pickupCorporateSummary: PickupCorporateSummary = new PickupCorporateSummary()
  public isCancelPickupSuccess = false
  public isSuccessExportCRRNFD = false;
  public isLoadingExportCorporate = false
  public isSuccessExportCorporate = false;
  public isLoadingCRRNFD = false;
  public isLoadingCRROTW = false;
  public assignPickupBulkHistoryList: AssignPickupBulkHistoryList = new AssignPickupBulkHistoryList(
    new AssignPickupBulkHistoryPagination(),
    []
  );
  public pickupCorporateNfdDeadlineSummary: PickupCorporateNfdDeadlineSummary = new PickupCorporateNfdDeadlineSummary();
  public isExportCorporateLoading = false;
  public isLoadingPickupCorporateTable = false

  @Action({ rawError: true })
  public async fetchNearbyCourierList(params: Record<string, string | number>) {
    this.setNearbyCourierData([])
    this.setLoading(true)

    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )

    this.presenter
      .getNearbyCourier(formattedParams)
      .then(res => {
        this.setNearbyCourierData(res)
      })
      .catch(error => {
        Vue.notify({
          title: 'Fetch Nearby Courier Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public async fetchPickupList(
    params: Record<string, string | number | boolean | string[]>
  ) {
    this.setLoading(true)

    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )

    this.presenter
      .getAll(formattedParams)
      .then(res => {
        this.setPickupData(res)
      })
      .catch(error => {
        Vue.notify({
          title: 'Fetch Pickup Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public async fetchPickupCRRNFDList(
    params: Record<string, string | number | boolean | string[]>
  ): Promise<Pickups> {

    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )

    return new Promise((resolve, reject) => {
      this.presenter
        .getPickupCRRNFD(formattedParams)
        .then(res => {
          this.setCRRNFDData(res)
          resolve(res);
        })
        .catch(error => {
          reject(error);
          Vue.notify({
            title: 'Fetch Pickup Failed',
            text:
              error.status === 400 || error.status === 422
                ? error.error.message.en
                : 'Something wrong',
            type: 'error',
            duration: 5000,
          })
        })
    })
  }

  @Action({ rawError: true })
  public async fetchPickupCRRDONList(
    params: Record<string, string | number | boolean | string[]>
  ) {
    this.setLoading(true)

    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )

    this.presenter
      .getPickupCRRDON(formattedParams)
      .then(res => {
        this.setPickupData(res)
      })
      .catch(error => {
        Vue.notify({
          title: 'Fetch Pickup Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public getPickupDetail(params: {shipmentId: string, isAllData: boolean}) {
    this.setLoading(true)
    this.presenter
      .get(params)
      .then(res => {
        this.setPickupDetail(res)
      })
      .catch(error => {
        Vue.notify({
          title: 'Fetch Pickup Detail Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
        this.setPickupDetail(new PickupDetail())
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public getPickupDetails(params: Record<string, string | Record<string, string>>) {
    this.setLoading(true)

    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )

    const courierPhones: Record<string, string> = params.courier_phones as Record<string, string>;

    this.presenter
      .getBulk(formattedParams)
      .then(async bulkRes => {
        const lastCourierIds: Array<number> = []

        bulkRes.forEach(item => {
          const lastCancelData = item.histories?.find(
            history => history.statusId === 'CRRCNC'
          )

          lastCourierIds.push(lastCancelData?.actorId || NaN)
        })

        await Promise.allSettled(
          lastCourierIds.map(async courierId => {
            if (!courierId) return ''

            try {
              const courierRes = await this.manageCourierPresenter
                .getDetail(String(courierId))
                .then(res => {
                  return `[${res.courierId}] ${res.fullName} (${res.announcementCourierType}) ${res.phoneNumber || courierPhones[String(res.courierId)] as string || '********'} ${res.partnerName}`
                })
              return Promise.resolve(courierRes)
            } catch (e) {
              const error = e as {
                error: { message: { en: string; id: string } }
              }
              return Promise.reject({
                message: error.error.message.en,
              })
            }
          })
        ).then(response => {
          const resVal = response
            .filter(courier => courier.status === 'fulfilled')
            .map(courier => ((courier as unknown) as { value: string }).value)

          resVal.forEach((name, index) => {
            bulkRes[index].lastCancelCourier = name
          })
        })

        this.setPickupDetailBulk(bulkRes)
      })
      .catch(error => {
        Vue.notify({
          title: 'Fetch Pickup Detail Bulk Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public getPickupDetailForce(
    params: Record<string, string | number | boolean | string[]>
  ) {
    this.setLoading(true)
    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )
    this.presenter
      .getPickupDetail(formattedParams)
      .then(res => {
        this.setPickupDetailForce(res)
      })
      .catch(error => {
        Vue.notify({
          title: 'Fetch Pickup Detail Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public getPickupSnapshotDetail(id: string) {
    this.setLoading(true)
    this.presenter
      .getSnapshot(id)
      .then(res => {
        this.setPickupSnapshotDetail(res)
      })
      .catch(error => {
        if (error.status === 404) {
          this.setPickupSnapshotDetail([])
        } else {
          Vue.notify({
            title: 'Fetch Pickup Snapshot Detail Failed',
            text:
              error.status === 400 || error.status === 422
                ? error.error.message.en
                : 'Something wrong',
            type: 'error',
            duration: 5000,
          })
        }
      })
  }

  @Action({ rawError: true })
  public getPickupSummary(params?: Record<string, string>) {
    this.setLoading(true)
    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )
    this.presenter
      .getSummary(formattedParams)
      .then(res => {
        this.setPickupSummary(res)
      })
      .catch(error => {
        Vue.notify({
          title: 'Fetch Pickup Summary Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public exportPickup(form: { statusId: string; email: string }) {
    this.setExportLoading(true)

    this.presenter
      .export(new ExportPickupApiRequest(form.statusId, form.email))
      .then(urls => {
        urls.forEach(url => (window.location.href = url))
      })
      .catch(error => {
        Vue.notify({
          title: 'Export Pickup Data Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setExportLoading(false)
      })
  }

  @Action({ rawError: true })
  public exportCRRNFD(params: Record<string, string>) {
    this.setExportCRRNFDLoading(true)

    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )

    this.presenter
      .exportCRRNFD(formattedParams)
      .then(res => {
        this.setIsSuccessExportCRRNFD(true);
        Vue.notify({
          title: 'Export CRRNFD Success',
          text: 'CRRNFD Export Successfully',
          type: 'success',
          duration: 5000,
        })
        window.open(res)
      })
      .catch(error => {
        this.setIsSuccessExportCRRNFD(false);
        Vue.notify({
          title: 'Export Pickup Data Failed',
          text: [400, 422].includes(error.status)
            ? error.error.message.en
            : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setExportCRRNFDLoading(false)
      })
  }

  @Action({ rawError: true })
  public exportCorporate(params: Record<string, string>) {
    this.setLoadingExportCorporate(true)

    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )

    this.presenter
      .exportCRRNFD(formattedParams)
      .then(res => {
        this.setIsSuccessExportCorporate(true);
        Vue.notify({
          title: 'Export Corporate Success',
          text: 'Corporate Export Successfully',
          type: 'success',
          duration: 5000,
        })
        window.open(res)
      })
      .catch(error => {
        this.setIsSuccessExportCorporate(false);
        Vue.notify({
          title: 'Export Corporate Data Failed',
          text: [400, 422].includes(error.status)
            ? error.error.message.en
            : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoadingExportCorporate(false)
      })
  }

  @Action({ rawError: true })
  public async getPickupList(
    params: Record<string, string | number | string[] | boolean | undefined>
  ) {
    if (params.reset) this.setPickupData(undefined)
    delete params.reset

    this.setLatestSync(dayjs().toISOString())

    // If multiple status Ids
    if (params.statusIds) {
      const statusIdList: Array<string> = <string[]>params.statusIds

      statusIdList.forEach(async statusId => {
        await this.fetchPickupList({
          ...params,
          clear: true,
          statusId: statusId,
          statusIds: statusIdList,
        })
      })
    } else {
      if (params.statusId === 'CRRNFD') {
        await this.fetchPickupCRRNFDList({
          ...params,
          clear: false,
        })
      } else if (params.statusId === 'CRRDON') {
        await this.fetchPickupCRRDONList({
          ...params,
          clear: false,
        })
      } else {
        await this.fetchPickupList({
          ...params,
          clear: false,
        })
      }
    }
  }

  @Action({ rawError: true })
  public getCourierList(params: Record<string, string | number | undefined>) {
    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )

    this.setCourierLoading(true)
    this.podTrackerPresenter
      .getDrivers(formattedParams)
      .then(res => {
        this.setCourierData(res)
      })
      .catch(error => {
        Vue.notify({
          title: 'Fetch Courier Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.id
              : 'Terjadi kesalahan',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setCourierLoading(false)
      })
  }

  @Action({ rawError: true })
  public manualAssignee(form: {
    shipmentIds: string[]
    courierId: number
    statusId: string
    manualAssignDate?: string
    timeZone?: string,
    plateNo?: string
  }) {

    const payload = new ManualAssigneeApiRequest({
      ids: form.shipmentIds,
      courierId: form.courierId,
      manualAssignDate: form.manualAssignDate,
      timeZone: form.timeZone,
      plateNo: form.plateNo
    })

    this.setLoading(true)

    const presenter: PickupPresenter = container.resolve(PickupPresenter)
    presenter
      .manualAssignee(payload)
      .then(() => {
        this.setRequestManualAssign(EventBusConstants.MANUAL_ASSIGNEE_SUCCESS)
      })
      .catch(error => {
        Vue.notify({
          title: 'Manual Assignee Failed',
          text:
            error.status === 400 || error.status === 422 || error.status === 404
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public markAsDone(form: MarkAsDoneForm) {
    this.setLoading(true)

    const presenter: PickupPresenter = container.resolve(PickupPresenter)
    presenter
      .markAsDone(
        new MarkAsDoneApiRequest(
          form.groupId,
          form.shipmentId,
          form.courierId,
          form.quantity,
          form.hubPersonName,
          form.personName,
          form.relationId
        ),
        !!form.groupId
      )
      .then(() => {
        EventBus.$emit(EventBusConstants.PICKUP_MARK_AS_DONE_SUCCESS)
      })
      .catch(error => {
        Vue.notify({
          title: 'Mark As Done Failed',
          text:
            error.status === 400 || error.status === 422 || error.status === 404
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public cancelPickupNew(form: { shipmentId: string; note: string }) {
    const presenter: PickupPresenter = container.resolve(PickupPresenter)
    return presenter
      .cancelPickup(
        new CancelPickupApiRequest(form.shipmentId, 'ZZZZZ', form.note)
      )
  }


  @Action({ rawError: true })
  public cancelPickup(form: { shipmentId: string; note: string }) {
    this.setLoading(true)

    const presenter: PickupPresenter = container.resolve(PickupPresenter)
    presenter
      .cancelPickup(
        new CancelPickupApiRequest(form.shipmentId, 'ZZZZZ', form.note)
      )
      .then(() => {
        this.setIsCancelPickupSuccess(true)
      })
      .catch(error => {
        Vue.notify({
          title: 'Cancel Pickup Failed',
          text:
            error.status === 400 || error.status === 422 || error.status === 404
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public pickupDedicatedCRROTW(form: {
    taskId: number
    shipmentId: string
    actorId: number
    email: string
    from: string
  }) {
    this.setLoading(true)

    const presenter: PickupPresenter = container.resolve(PickupPresenter)
    presenter
      .pickupDedicatedCRROTW(
        new PickupDedicatedCRROTWRequest(
          form.taskId,
          form.shipmentId,
          form.actorId,
          form.email,
          form.from
        )
      )
      .then(() => {
        this.setIsSuccessForce(true)
      })
      .catch(error => {
        Vue.notify({
          title: 'Cancel Pickup Failed',
          text:
            error.status === 400 || error.status === 422 || error.status === 404
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public async getPickupAdditionalTime(
    params: Record<string, string | number | boolean>
  ) {
    if (params.reset) {
      this.setPickupAdditionalTime(undefined)
    }

    delete params.reset

    this.setLoading(true)
    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )
    this.presenter
      .getPickupAdditionalTime(formattedParams)
      .then(res => {
        this.setPickupAdditionalTime(res)
      })
      .catch(error => {
        Vue.notify({
          title: 'Fetch Pickup Additional Time Failed',
          text:
            error.status === 400 || error.status === 422 || error.status === 404
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public async exportPickupList(
    params: Record<string, string | number | boolean | string[] | undefined>
  ) {
    this.setExportPickupListLoading(true)

    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )

    this.presenter
      .exportPickupList(formattedParams)
      .then(urls => {
        window.open(urls)
      })
      .catch(error => {
        Vue.notify({
          title: 'Export Pickup Data Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setExportPickupListLoading(false)
      })
  }

  @Action({ rawError: true })
  public async getPickupCRROTW(
    params: Record<string, string | number | boolean>
  ) {
    this.setLoading(true)
    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )

    if (params.syncSummary) {
      this.setLatestSync(new Date().toISOString())
    }

    this.presenter
      .getListCRROTW(formattedParams)
      .then(res => {
        this.setPickupCRROTW({
          payload: res,
          sync: <boolean>params.syncSummary,
        })
      })
      .catch(error => {
        Vue.notify({
          title: 'Fetch Pickup CRROTW Failed',
          text:
            error.status === 400 || error.status === 422 || error.status === 404
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public async getPickupCRROTWV2(
    params: Record<string, string | number | boolean>
  ) {
    this.setIsLoadingCRROTW(true)

    if (params.syncSummary) {
      this.setLatestSync(new Date().toISOString())
    }

    const listParams = buildCrrotwV2ListParams(params)
    const summaryPromise = params.syncSummary
      ? this.presenter.getSummaryCRROTWV2(buildCrrotwV2SummaryParams(params))
      : Promise.resolve(null)

    Promise.all([
      this.presenter.getListCRROTWV2(listParams),
      summaryPromise,
    ])
      .then(([list, summary]) => {
        this.setPickupCRROTWV2(list)
        if (summary) {
          this.setPickupCRROTWSummaryV2(summary)
        }
      })
      .catch(error => {
        Vue.notify({
          title: 'Fetch Pickup CRROTW Failed',
          text:
            error.status === 400 || error.status === 422 || error.status === 404
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setIsLoadingCRROTW(false)
      })
  }

  /**
   * POST /v1/shipment/admin/task/pickup/cancel — task group (GENESIS-11351).
   * CRRNFD: restart_nearest_courier true (admin single path). CRRCNC: false.
   * Rejects on API error so the view can show an error modal; caller should catch.
   */
  @Action({ rawError: true })
  public async cancelCrrotwTaskGroupAdmin(payload: {
    taskGroupId: number
    reason: string
    restartNearestCourier: boolean
  }): Promise<void> {
    this.setLoading(true)
    try {
      const presenter: PickupPresenter = container.resolve(PickupPresenter)
      const trimmed = payload.reason.trim()
      await presenter.adminTaskPickupCancel(
        new AdminTaskPickupCancelApiRequest(
          payload.taskGroupId,
          trimmed,
          payload.restartNearestCourier
        )
      )
    } finally {
      this.setLoading(false)
    }
  }

  /**
   * Merges ACTIVE couriers for driver_type POS and KVP with the same base query.
   * `not_in` carries comma-separated courier ids to exclude (current assignees).
   */
  @Action({ rawError: true })
  public async fetchCrrotwTransferCourierOptions(
    notInCourierIds: number[]
  ): Promise<ManageCourier[]> {
    const filtered = [...new Set(notInCourierIds.filter(id => id > 0))]
    const base = new Map<string, string | number>([
      ['courier_status', 'ACTIVE'],
      ['per_page', 500],
    ])
    if (filtered.length) {
      base.set('not_in', filtered.join(','))
    }
    const driverTypes = ['POS', 'KVP']
    const byId = new Map<number, ManageCourier>()
    for (const dt of driverTypes) {
      const params = new Map(base)
      params.set('driver_type', dt)
      const batch = await this.presenter.getCouriersForDashboardPickupTransfer(
        params
      )
      for (const c of batch.data ?? []) {
        if (c.courierId != null) {
          byId.set(c.courierId, c)
        }
      }
    }
    return [...byId.values()].sort((a, b) =>
      String(a.fullName ?? '').localeCompare(String(b.fullName ?? ''), 'id')
    )
  }

  @Action({ rawError: true })
  public async submitCrrotwDashboardPickupTransfer(payload: {
    taskGroupIds: number[]
    courierId: number
  }): Promise<DashboardPickupTransferOutcome[]> {
    this.setLoading(true)
    let userName: string | undefined
    try {
      const role = LocalStorage.getLocalStorage(LocalStorage.MY_ROLE, true)
      if (role === 'GENESIS') {
        try {
          const id = LocalStorage.getLocalStorage(
            LocalStorage.LOGIN_IDENTITY,
            true
          )
          if (id) {
            userName = String(id)
          }
        } catch {
          userName = undefined
        }
      }
    } catch {
      userName = undefined
    }

    const req = new DashboardPickupTransferRequest(
      payload.taskGroupIds,
      payload.courierId,
      userName
    )
    try {
      return await this.presenter.transferDashboardPickup(req)
    } finally {
      this.setLoading(false)
    }
  }

  /**
   * POST /v1/shipment/admin/task/pickup/done — task_group_ids (+ optional excluded_shipment_ids).
   * Halaman satu grup (GENESIS-11183): sertakan hub_person_name & person_name dari form.
   * Bulk toolbar: hanya task_group_ids (tanpa field hub di UI).
   */
  @Action({ rawError: true })
  public async submitCrrotwAdminTaskPickupDone(payload: {
    taskGroupIds: number[]
    excludedShipmentIds?: string[]
    hubPersonName?: string
    personName?: string
  }): Promise<boolean> {
    this.setLoading(true)
    try {
      await this.presenter.adminTaskPickupDone(
        new AdminTaskPickupDoneApiRequest(
          payload.taskGroupIds,
          payload.excludedShipmentIds,
          payload.hubPersonName,
          payload.personName
        )
      )
      return true
    } catch (error: unknown) {
      const err = error as {
        status?: number
        data?: { message?: { en?: string } }
        error?: { message?: { en?: string } }
      }
      const en =
        err?.data?.message?.en ?? err?.error?.message?.en ?? ''
      Vue.notify({
        title: 'Tandai Selesai',
        text:
          err?.status === 400 ||
          err?.status === 422 ||
          err?.status === 403 ||
          err?.status === 404
            ? String(en || 'Validasi gagal.')
            : 'Tandai selesai gagal.',
        type: 'error',
        duration: 6000,
      })
      return false
    } finally {
      this.setLoading(false)
    }
  }

  @Action({ rawError: true })
  public async forceCRRCNC(params: { shipmentId: string; reason: string }) {
    this.setLoading(true)
    const courierId = this.pickupCRROTWData.data?.find(
      item => item.shipmentId === params.shipmentId
    )?.courierPickupDetail?.courierId

    const payload = new PickupForceCRRCNCRequest(
      params.shipmentId,
      <number>courierId,
      params.reason
    )

    const presenter: PickupPresenter = container.resolve(PickupPresenter)

    presenter
      .forceCRRCNC(payload)
      .then(() => {
        this.setIsSuccessForceCRRCNC(true)
      })
      .catch(error => {
        Vue.notify({
          title: 'Force CRRCNC Failed',
          text:
            error.status === 400 || error.status === 422 || error.status === 404
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
        this.setIsFailedForceCRRCNC(true)
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public async forceCRRCNCV2(params: { shipmentId: string; reason: string }) {
    this.setLoading(true)
    let courierId: number | undefined
    for (const g of this.pickupCRROTWV2Data.data ?? []) {
      if (g.shipments.some(s => s.shipmentId === params.shipmentId)) {
        courierId = g.courier.id
        break
      }
    }

    const payload = new PickupForceCRRCNCRequest(
      params.shipmentId,
      <number>courierId,
      params.reason
    )

    const presenter: PickupPresenter = container.resolve(PickupPresenter)

    presenter
      .forceCRRCNC(payload)
      .then(() => {
        this.setIsSuccessForceCRRCNC(true)
      })
      .catch(error => {
        Vue.notify({
          title: 'Force CRRCNC Failed',
          text:
            error.status === 400 || error.status === 422 || error.status === 404
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
        this.setIsFailedForceCRRCNC(true)
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public async updateIsContactedCRROTW(
    params: Record<string, string | number | boolean>
  ) {
    this.setLoading(true)
    const payload = new UpdateIsContactedCRROTWRequest(<number>params.status)
    this.presenter
      .updateIsContactedCRROTW(<number>params.taskId, payload)
      .then(() => {
        this.successUpdateIsContactedCRROTWV2({
          bool: true,
          index: <number>params.index,
        })
      })
      .catch(error => {
        Vue.notify({
          title: 'Update isContacted CRROTW Failed',
          text:
            error.status === 400 || error.status === 422 || error.status === 404
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public async updateIsContactedCRRQUE(
    params: Record<string, string | number | boolean>
  ) {
    this.setLoading(true)
    const payload = new UpdateIsContactedCRRQUERequest(
      <boolean>params.isContacted
    )
    this.presenter
      .updateIsContactedCRRQUE(<string>params.shipmentId, payload)
      .then(() => {
        this.successUpdateIsContactedCRRQUE({
          bool: true,
          index: <number>params.index,
        })
      })
      .catch(error => {
        Vue.notify({
          title: 'Update isContacted CRRQUE Failed',
          text:
            error.status === 400 || error.status === 422 || error.status === 404
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public async exportCRROTW(params: Record<string, string | number | boolean>) {
    this.setExportCRROTWLoading(true)
    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )

    this.presenter
      .exportCRROTW(formattedParams)
      .then(urls => {
        window.open(urls)
      })
      .catch(error => {
        Vue.notify({
          title: 'Export CRROTW Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setExportCRROTWLoading(false)
      })
  }

  @Action({ rawError: true })
  public async forceCRRSRC(shipmentId: string): Promise<void> {
    this.setLoadingForceCRRSRC(true)
    const payload = new ForceCRRSRCRequest(shipmentId)

    const presenter: PickupPresenter = container.resolve(PickupPresenter)
    presenter
      .forceCRRSRC(payload)
      .then(() => {
        this.setIsForceCRRSRCSuccess(true)
      })
      .catch(error => {
        Vue.notify({
          title: 'Force CRRSRC Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoadingForceCRRSRC(false)
      })
  }

  @Action({ rawError: true })
  public async getPickupCRRQUE(
    params: Record<string, string | number | boolean>
  ) {
    this.setLoading(true)

    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )

    this.presenter
      .getListCRRQUE(formattedParams)
      .then(res => {
        this.setLatestSync(new Date().toISOString())
        this.setPickupCRRQUE({
          data: res,
          sync: <boolean>params.summary,
        })
      })
      .catch(error => {
        Vue.notify({
          title: 'Fetch Pickup CRRQUE Failed',
          text:
            error.status === 400 || error.status === 422 || error.status === 404
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public async forceCancelCRRQUE(params: {
    shipmentId: string
    note: string
    status: string
  }): Promise<void> {
    this.setLoading(true)

    const payload = new ForceCancelCRRQUERequest(params.shipmentId, params.note)

    const presenter: PickupPresenter = container.resolve(PickupPresenter)
    presenter
      .forceCancelCRRQUE(payload)
      .then(() => {
        this.setIsSuccessForceCancelCRRQUE(true)
      })
      .catch(error => {
        Vue.notify({
          title: `Force ${params.status} Failed`,
          text:
            error.status === 400 || error.status === 422 || error.status === 404
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
        this.setIsFailedForceCancelCRRQUE(true)
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public async exportCRRQUE(params: Record<string, string | number | boolean>) {
    this.setExportCRRQUELoading(true)
    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )

    this.presenter
      .exportCRRQUE(formattedParams)
      .then(urls => {
        window.open(urls)
      })
      .catch(error => {
        Vue.notify({
          title: 'Export CRRQUE Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setExportCRRQUELoading(false)
      })
  }

  @Action({ rawError: true })
  public async fetchCRRNFDSummary() {
    return new Promise((resolve, reject) => {
      this.presenter
        .getCRRNFDSummary()
        .then(data => {
          this.setCRRNFDSummaryData(data)
          resolve(data);
        })
        .catch(error => {
          reject(error);
          Vue.notify({
            title: 'Fetch CRRNFD Failed',
            text:
              error.status === 400 || error.status === 422
                ? error.error.message.en
                : 'Something wrong',
            type: 'error',
            duration: 5000,
          })
        })
    })
  }

  @Action({ rawError: true })
  public async fetchPickupNfdList(
    params: Record<string, string | number | boolean | undefined>
  ): Promise<PickupNfdList> {
    const parameters = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      "snake_case"
    );
    return new Promise((resolve, reject) => {
      this.presenter
        .getPickupNfdList(parameters)
        .then(res => {
          this.setPickupNfdListData(res)
          resolve(res)
        })
        .catch(error => {
          reject(error)
          Vue.notify({
            title: 'Fetch Pickup CRRNFD Failed',
            text:
              error.status === 400 || error.status === 422
                ? error.error.message.en
                : 'Something wrong',
            type: 'error',
            duration: 5000,
          })
        })
    })
  }

  @Action({ rawError: true })
  public async fetchPickupNfdSummary(
    params: Record<string, string | number | boolean | undefined>
  ): Promise<PickupNfdSummary> {
    const map = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      "snake_case"
    )
    return new Promise((resolve, reject) => {
      this.presenter
        .getPickupNfdSummary(map)
        .then(data => {
          this.setPickupNfdSummaryData(data)
          resolve(data)
        })
        .catch(error => {
          reject(error)
          Vue.notify({
            title: 'Fetch CRRNFD Summary Failed',
            text:
              error.status === 400 || error.status === 422
                ? error.error.message.en
                : 'Something wrong',
            type: 'error',
            duration: 5000,
          })
        })
    })
  }

  @Action({ rawError: true })
  public async fetchPickupNfdByShipperHash(
    payload: {
      shipperHash: string
      params: Record<string, string | number | boolean | undefined>
    }
  ): Promise<PickupNfdShipperHashDetail> {
    const hash = String(payload.shipperHash || '').trim()
    const map = buildPickupNfdByHashDetailParams({
      page: payload.params.page ?? 1,
      perPage: payload.params.perPage ?? 10,
      sortField: payload.params.sortField,
      sortDirection: payload.params.sortDirection,
      isShowDetail: payload.params.isShowDetail,
    })
    const parameters = Utils.toInstance(
      new Map(),
      JSON.stringify(Object.fromEntries(map)),
      "snake_case"
    );

    this.setPickupNfdShipperDetailLoading(true)
    return new Promise((resolve, reject) => {
      this.presenter
        .getPickupNfdByShipperHash(hash, parameters)
        .then(res => {
          this.setPickupNfdShipperHashDetailData(res)
          resolve(res)
        })
        .catch(error => {
          const enMsg =
            error?.error?.message?.en ?? error?.data?.message?.en ?? ''
          const isCancelled = enMsg === 'Cancel by User'
          if (error.status === 404) {
            this.clearPickupNfdShipperHashDetail()
          }
          reject(error)
          if (isCancelled) {
            return
          }
          if (error.status === 404) {
            Vue.notify({
              title: 'Tidak ditemukan',
              text:
                error.error?.message?.id ??
                'Data pengirim tidak ditemukan atau di luar cakupan Liberta.',
              type: 'error',
              duration: 6000,
            })
          } else if (error.status === 400 || error.status === 422) {
            Vue.notify({
              title: 'Gagal memuat detail pengirim',
              text: error.error.message.id,
              type: 'error',
              duration: 5000,
            })
          } else if (error.status != null) {
            Vue.notify({
              title: 'Gagal memuat detail pengirim',
              text: 'Something wrong',
              type: 'error',
              duration: 5000,
            })
          }
        })
        .finally(() => {
          this.setPickupNfdShipperDetailLoading(false)
        })
    })
  }

  @Mutation
  public clearPickupNfdShipperHashDetail(): void {
    this.pickupNfdShipperHashDetail = null
  }

  @Mutation
  private setPickupNfdShipperHashDetailData(
    data: PickupNfdShipperHashDetail
  ): void {
    this.pickupNfdShipperHashDetail = data
  }

  @Mutation
  private setPickupNfdShipperDetailLoading(loading: boolean): void {
    this.isLoadingPickupNfdShipperDetail = loading
  }

  @Action({ rawError: true })
  public async postPickupNfdShipperHashToCrrsrc(
    shipperHashes: string[]
  ): Promise<PickupShipperHashOutcome[]> {
    const hashes = shipperHashes.map(h => String(h).trim()).filter(Boolean)
    if (hashes.length === 0) {
      return []
    }
    return new Promise((resolve, reject) => {
      this.setLoading(true);
      this.presenter
        .postPickupNfdShipperHashToCrrsrc(hashes)
        .then(res => {
          resolve(res)
        })
        .catch(error => {
          reject(error)
          Vue.notify({
            title: 'Ubah ke CRRSRC',
            text:
              error.status === 400 || error.status === 422
                ? error.error.message.en
                : 'Something wrong',
            type: 'error',
            duration: 5000,
          })
        }).finally(() => {
          this.setLoading(false);
        })
    })
  }

  @Action({ rawError: true })
  public async manualAssignPickupBulkLiberta(form: {
    shipperHashes: PickupAssignIds[]
    courierId: number
    manualAssignDate?: string
    timeZone?: string
  }): Promise<PickupShipperHashOutcome[]> {
    const payload = new ManualAssigneeBulkApiRequest({
      ids: form.shipperHashes,
      courierId: form.courierId,
      manualAssignDate: form.manualAssignDate,
      timeZone: form.timeZone,
    })
    return new Promise((resolve, reject) => {
      this.presenter
        .manualAssignPickupBulkV2(payload)
        .then(results => {
          resolve(results)
        })
        .catch(error => {
          reject(error)
          Vue.notify({
            title: 'Assign multiple ke kurir',
            text:
              error.status === 400 || error.status === 422 || error.status === 404
                ? error.error.message.id
                : 'Terjadi kesalahan',
            type: 'error',
            duration: 5000,
          })
        })
    })
  }

  @Action({ rawError: true })
  public async fetchAssignPickupBulkHistoryList(
    params: Record<string, string | number | boolean | undefined>
  ): Promise<AssignPickupBulkHistoryList> {
    const parameters = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      "snake_case"
    );
    return new Promise((resolve, reject) => {
      this.presenter
        .getAssignPickupBulkHistory(parameters)
        .then(res => {
          this.setAssignPickupBulkHistoryListData(res)
          resolve(res)
        })
        .catch(error => {
          reject(error)
          Vue.notify({
            title: 'Fetch Assign Pickup Bulk History Failed',
            text:
              error.status === 400 || error.status === 422
                ? error.error.message.en
                : 'Something wrong',
            type: 'error',
            duration: 5000,
          })
        })
    })
  }

  @Mutation
  private setAssignPickupBulkHistoryListData(data: AssignPickupBulkHistoryList): void {
    this.assignPickupBulkHistoryList = data
  }

  @Action({ rawError: true })
  public async readAssignPickupBulkHistory(form: {
    shipperHashes: string[]
  }): Promise<boolean> {
    const payload = new ReadAssignPickupBulkHistoryRequest(
      form.shipperHashes
    )
    return new Promise((resolve, reject) => {
      this.presenter
        .readAssignPickupBulkHistory(payload)
        .then(results => {
          resolve(results)
        })
        .catch(error => {
          reject(error)
          Vue.notify({
            title: 'Read Assign pickup bulk history',
            text:
              error.status === 400 || error.status === 422 || error.status === 404
                ? error.error.message.id
                : 'Terjadi kesalahan',
            type: 'error',
            duration: 5000,
          })
        })
    })
  }

  @Action({ rawError: true })
  public async exportManualAssignLog(params: Record<string, string>) {
    this.setLoadingExportManualAssign(true)
    this.setIsSuccessExportManualAssignLog(false)
    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )

    this.presenter
      .exportManualAssignLog(formattedParams)
      .then(urls => {
        this.setIsSuccessExportManualAssignLog(true)
        Vue.notify({
          title: 'Export Manual Assign Success',
          text: 'Manual Assign Log Export Successfully',
          type: 'success',
          duration: 5000,
        })
        window.open(urls)
      })
      .catch(error => {
        Vue.notify({
          title: 'Export Manual Assign Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoadingExportManualAssign(false)
      })
  }

  @Action({ rawError: true })
  public fetchPickupCorporateList(
    params: Record<string, string | number | boolean>
  ): void {
    this.setLoading(true)

    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )

    this.presenter
      .getListCorporate(formattedParams)
      .then(res => {
        this.setLatestSync(new Date().toISOString())
        this.setPickupCorporateData(res)
      })
      .catch(error => {
        Vue.notify({
          title: 'Fetch Pickup Corporate List Failed',
          text: [400, 402].includes(error.status)
            ? error.error.message.en
            : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public fetchPickupCorporateListV2(
    params: Record<string, string | number | boolean>
  ): void {
    this.setLoadingPickupCorporateTable(true)
    this.bumpPickupCorporateListV2RequestToken()
    const requestToken = this.pickupCorporateListV2RequestToken

    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )

    this.presenter
      .getListCorporateV2(formattedParams)
      .then(res => {
        // Drop stale response: a newer request has been issued since (e.g. tab switch).
        if (requestToken !== this.pickupCorporateListV2RequestToken) return
        this.setPickupCorporateDataV2(res)
      })
      .catch(error => {
        if (requestToken !== this.pickupCorporateListV2RequestToken) return
        Vue.notify({
          title: 'Fetch Pickup Corporate List Failed',
          text: [400, 402].includes(error.status)
            ? error.error.message.en
            : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        // Only the latest request controls the loading flag, so an early-returning
        // stale response does not prematurely clear the spinner for the active one.
        if (requestToken !== this.pickupCorporateListV2RequestToken) return
        this.setLoadingPickupCorporateTable(false)
      })
  }

  @Action({ rawError: true })
  public async fetchCorporateSummary() {
    this.setLoading(true)
    this.presenter
      .getCorporateSummary()
      .then(data => {
        this.setPickupCorporateSummary(data)
      })
      .catch(error => {
        Vue.notify({
          title: 'Fetch Corporate Summary Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public getExportPickup(payload: ExportPickupRequest) {
    return new Promise((resolve, reject) => {
    this.presenter
      .getExportPickup(payload)
      .then(async (res) => {
          resolve(res)
          await MainController.prependReportList(res)
          MainController.setReportData(MainController.reportData);
      })
      .catch(error => {
        reject(error);
        Vue.notify({
          title: `Export Pickup ${payload.statusId} Failed`,
          text:
            error.status === 400 || error.status === 422
              ? error?.error?.message?.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
    })
  }

  @Action({ rawError: true })
  public getCouriers(params: Record<string, string | number>) {
    const presenter = container.resolve(ManageCourierPresenter);
    const parameter = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )
    return presenter.getCouriersAdjustSaldo(parameter);
  }

  @Mutation
  private setNearbyCourierData(couriers: NearbyCourier[]) {
    this.nearbyCourierData = couriers
  }

  @Mutation
  private setPickupData(pickups: Pickups | undefined) {
    if (pickups) {
      this.paginationData = pickups.pagination
      this.pickupData = [...this.pickupData, ...pickups.data]
    } else {
      this.paginationData = new Pagination()
      this.pickupData = []
    }
  }

  @Mutation
  private setCRRNFDData(pickups: Pickups): void {
    this.pickupData = pickups.data
    this.paginationData = pickups.pagination
  }

  @Mutation
  public setPickupDetail(pickup: PickupDetail) {
    this.pickupDetail = pickup
  }

  @Mutation
  private setPickupDetailBulk(pickup: PickupDetail[]) {
    this.pickupDetails = pickup
  }

  @Mutation
  private setPickupDetailForce(pickup: PickupForceDetail) {
    this.pickupDetailForce = pickup
  }

  @Mutation
  private setPickupSnapshotDetail(snapshot: PickupDetailSnapshot[]) {
    this.pickupSnapshotDetail = snapshot
  }

  @Mutation
  private setPickupSummary(pickup: PickupSummary) {
    this.pickupSummary = pickup
  }

  @Mutation
  private setCourierData(couriers: PODTrackerDrivers) {
    this.courierData = (couriers.data || []).map(courier => ({
      label: `[${courier.courierId}] ${courier.fullname} (${courier.announcementCourierType}) ${courier.phoneNumber} • ${courier.partnerName}`,
      value: courier.courierId as number,
    }))
  }

  @Mutation
  public setLatestSync(latestSync: string | null) {
    this.latestSync = latestSync
  }

  @Mutation
  private setExportLoading(bool: boolean) {
    this.isExportLoading = bool
  }

  @Mutation
  public setExportCRRNFDLoading(bool: boolean) {
    this.isExportCRRNFDLoading = bool
  }

  @Mutation
  public setExportCRROTWLoading(bool: boolean) {
    this.isExportCRROTWLoading = bool
  }

  @Mutation
  public setExportCRRQUELoading(bool: boolean) {
    this.isExportCRRQUELoading = bool
  }

  @Mutation
  public setExportCRRDONLoading(bool: boolean) {
    this.isExportCRRDONLoading = bool
  }

  @Mutation
  public setExportCRRPICLoading(bool: boolean) {
    this.isExportCRRPICLoading = bool
  }

  @Mutation
  public setExportDOXLoading(bool: boolean) {
    this.isExportDOXLoading = bool
  }

  @Mutation
  private setCourierLoading(bool: boolean) {
    this.isCourierLoading = bool
  }

  @Mutation
  public setLoading(bool: boolean) {
    this.isLoading = bool
  }

  @Mutation
  public setIsSuccessForce(bool: boolean) {
    this.isSuccessForce = bool
  }

  @Mutation
  public setPickupBulkData(payload: PickupBulkDataState | null) {
    this.pickupBulkData = payload
  }

  @Mutation
  public setRequestManualAssign(message: string) {
    this.requestManualAssign = message
  }

  @Mutation
  public setPickupAdditionalTime(payload: PickupAdditionalTimes | undefined) {
    if (payload) {
      this.paginationData = payload.pagination
      this.pickupAdditionalTimeData = payload.data
    } else {
      this.paginationData = new Pagination()
      this.pickupAdditionalTimeData = []
    }
  }

  @Mutation
  private setExportPickupListLoading(bool: boolean) {
    this.isexportPickupListLoading = bool
  }

  @Mutation
  public setPickupCRROTW(payload: { payload: PickupCRROTWs; sync: boolean }) {
    this.pickupCRROTWData.data = payload.payload.data || []
    this.pickupCRROTWData.pagination = payload.payload.pagination
    if (payload.sync) {
      this.pickupCRROTWData.summary = payload.payload.summary
    }
  }

  @Mutation
  public setPickupCRROTWV2(payload: PickupCRROTWV2List) {
    this.pickupCRROTWV2Data = payload
  }

  @Mutation
  public setPickupCRROTWSummaryV2(payload: PickupCRROTWV2Summary) {
    this.pickupCRROTWSummaryV2 = payload
  }

  @Mutation
  public setIsSuccessForceCRRCNC(bool: boolean) {
    this.isSuccessForceCRRCNC = bool
  }

  @Mutation
  public setIsFailedForceCRRCNC(bool: boolean) {
    this.isFailedForceCRRCNC = bool
  }

  @Mutation
  public successUpdateIsContactedCRROTWV2(payload: {
    bool: boolean
    index: number
  }) {
    if (payload.bool) {
      const row = this.pickupCRROTWV2Data.data?.[payload.index]
      if (row) {
        row.isContacted = !row.isContacted
      }
    }
  }

  @Mutation
  public successUpdateIsContactedCRRQUE(payload: {
    bool: boolean
    index: number
  }) {
    if (payload.bool) {
      if (this.pickupCRRQUEData.data?.length) {
        this.pickupCRRQUEData.data[payload.index].isContacted = !this
          .pickupCRRQUEData.data[payload.index].isContacted
      }
    }
  }

  @Mutation
  public setIsForceCRRSRCSuccess(bool: boolean): void {
    this.isForceCRRSRCSuccess = bool
  }

  @Mutation
  public setLoadingForceCRRSRC(bool: boolean): void {
    this.isLoadingForceCRRSRC = bool
  }

  @Mutation
  private setPickupCRRQUE(payload: {
    data: PickupCRRQUEs
    sync: boolean
  }): void {
    this.pickupCRRQUEData.data = payload.data.data
    this.pickupCRRQUEData.pagination = payload.data.pagination
    if (payload.sync) {
      this.pickupCRRQUEData.summary = payload.data.summary
    }
  }

  @Mutation
  public setIsSuccessForceCancelCRRQUE(bool: boolean): void {
    this.isSuccessForceCancelCRRQUE = bool
  }

  @Mutation
  public setIsFailedForceCancelCRRQUE(bool: boolean): void {
    this.isFailedForceCancelCRRQUE = bool
  }

  @Mutation
  private setCRRNFDSummaryData(data: PickupCRRNFDSummary): void {
    this.CRRNFDSummaryData = data
  }

  @Mutation
  private setPickupNfdListData(data: PickupNfdList): void {
    this.pickupNfdList = data
    this.paginationData = data.pagination
  }

  @Mutation
  private setPickupNfdSummaryData(data: PickupNfdSummary): void {
    this.pickupNfdSummary = data
  }

  @Mutation
  private setLoadingExportManualAssign(bool: boolean): void {
    this.isLoadingExportManualAssignLog = bool
  }

  @Mutation
  public setIsSuccessExportManualAssignLog(bool: boolean): void {
    this.isSuccessExportManualAssignLog = bool
  }

  @Mutation
  public setPickupCorporateData(data: PickupCorporates): void {
    this.pickupCorporateData = data.data
    this.paginationData = data.pagination
  }

  @Mutation
  public setPickupCorporateDataV2(data: PickupCorporatesV2): void {
    this.pickupCorporateDataV2 = data.data
    this.paginationData = data.pagination
  }

  @Mutation
  public bumpPickupCorporateListV2RequestToken(): void {
    this.pickupCorporateListV2RequestToken += 1
  }

  @Mutation
  public bumpPickupCorporateNfdDeadlineSummaryRequestToken(): void {
    this.pickupCorporateNfdDeadlineSummaryRequestToken += 1
  }

  @Mutation
  public setPickupCorporateSummary(data: PickupCorporateSummary): void {
    this.pickupCorporateSummary = data
  }
  @Mutation
  public setIsCancelPickupSuccess(bool: boolean) {
    this.isCancelPickupSuccess = bool
  }

  @Mutation
  public setIsSuccessExportCRRNFD(bool: boolean): void {
    this.isSuccessExportCRRNFD = bool
  }

  @Mutation
  public setLoadingExportCorporate(bool: boolean): void {
    this.isLoadingExportCorporate = bool
  }

  @Mutation
  public setIsSuccessExportCorporate(bool: boolean): void {
    this.isSuccessExportCorporate = bool
  }

  @Mutation
  public setIsLoadingCRROTW(isLoading: boolean): void {
    this.isLoadingCRROTW = isLoading
  }

  @Mutation
  public setIsLoadingCRRNFD(isLoading: boolean): void {
    this.isLoadingCRRNFD = isLoading
  }

  @Action({ rawError: true })
  public async adminContactShow(payload: {resource: string, resourceId: string, field: string, source: string}) {
    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(payload),
      'snake_case'
    )
    try {
      const resp = await this.presenter.adminContactShow(formattedParams);
      return resp
    } catch (error: any) {
      Vue.notify({
        title: 'Show Phone Number or Address Failed',
        text:
        error.status === 400 || error.status === 422
          ? error?.error?.message?.en
          : 'Something wrong',
        type: 'error',
        duration: 5000,
      })
      return new AdminContactShow();
    }
  }

  @Action({ rawError: true })
  public async syncPickupCorporate() {
    const presenter = container.resolve(PickupPresenter);
    return presenter.syncPickupCorporate();
  }

  @Action({ rawError: true })
  public async fetchCorporateNfdDeadlineSummary(params: Record<string, string | number | boolean>) {
    this.setLoading(true)
    this.bumpPickupCorporateNfdDeadlineSummaryRequestToken()
    const requestToken = this.pickupCorporateNfdDeadlineSummaryRequestToken

    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )
    this.presenter
      .getNfdDeadlineSummary(formattedParams)
      .then(data => {
        if (requestToken !== this.pickupCorporateNfdDeadlineSummaryRequestToken) return
        this.setPickupCorporateNfdDeadlineSummary(data)
      })
      .catch(error => {
        if (requestToken !== this.pickupCorporateNfdDeadlineSummaryRequestToken) return
        Vue.notify({
          title: 'Fetch Corporate Summary Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        if (requestToken !== this.pickupCorporateNfdDeadlineSummaryRequestToken) return
        this.setLoading(false)
      })
  }

  @Mutation
  public setPickupCorporateNfdDeadlineSummary(data: PickupCorporateNfdDeadlineSummary): void {
    this.pickupCorporateNfdDeadlineSummary = data
  }

  @Action({ rawError: true })
  public getExportCorporate(params: Record<string, string | number | boolean>) {
    this.setisLoadingExportCorporate(true)

    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )

    return new Promise((resolve, reject) => {
    this.presenter
      .exportCorporate(formattedParams)
      .then(async (res) => {
          resolve(res)
          await MainController.prependReportList(res)
          MainController.setReportData(MainController.reportData);
      })
      .catch(error => {
        reject(error);
        Vue.notify({
          title: `Export Pickup Corporate Failed`,
          text:
            error.status === 400 || error.status === 422
              ? error?.error?.message?.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setisLoadingExportCorporate(false)
      })
    })
  }

  @Mutation
  public setisLoadingExportCorporate(isLoading: boolean): void {
    this.isLoadingExportCorporate = isLoading
  }

  @Mutation
  public setLoadingPickupCorporateTable(bool: boolean) {
    this.isLoadingPickupCorporateTable = bool
  }
}

export default getModule(PickupController)
